<template>
  <collapsible-section class='_pt0 _pb0 -stretch-actions'>
    <template #headerText>
      <p>{{ title }}</p>
    </template>
    <template #headerActions>
      <app-search-input
        v-if="groupsSelected"
        label="Filter Groups"
        name="filterGroups"
        v-model="filterGroups" />
      <router-link
        :to="{ name: 'user-add-sub-group', params: { userId: $route.params.userId } }"
        v-if="!readonly && !myProfile"
        class='btn white --sm _mrxs'
      >
        Add A Group
      </router-link>
      <button
        v-if="!readonly && groupsSelected"
        @click="selectAll"
        class='btn white --sm'
        :class="{'_mrxs': myProfile }"
      >
        Select All
      </button>
      <button
        v-if="internalCollapsed && groupsSelected"
        @click="toggleAll(false)"
        class='btn white --sm _mrxs'
      >
        Open All
      </button>
      <button
        v-else-if="groupsSelected"
        @click="toggleAll(true)"
        class='btn white --sm _mrxs'
      >
        Collapse All
      </button>
    </template>
    <template #collapsibleSection>
      <div container>
        <cloud-cannon-content
          v-if="!viewOnly"        
          class="_p0 bg-light-gray"
          :content="cloudCannonContent.pending_permissions"
          :content-loading="cloudCannonContentLoading"
        />
        <div v-if="!readonly" class="acl-table">
          <table class="acl-table-header -header">
            <tr class="group-header-row">
              <td>
              </td>
              <td>
                <div class="cell">
                  <app-checkbox
                    :value="allSubGroupsHaveAcess('memberSearchAllowed')"
                    @click="toggleAccessForAllSubGroups('memberSearchAllowed', $event)"
                    :valueKey='`memberSearch`'
                    label='Member View'
                  />
                </div>
              </td>
              <td>
                <div class="cell -acl">
                  <app-checkbox
                    :valueKey='`memberMaintenance`'
                    :value="allSubGroupsHaveAcess('memberMaintenanceAllowed')"
                    @click="toggleAccessForAllSubGroups('memberMaintenanceAllowed', $event)"
                    label='Member Management'
                  />
                </div>
              </td>
              <td>
                <div class="cell -acl">
                  <app-checkbox
                    :value="allSubGroupsHaveAcess('enrollmentAllowed')"
                    @click="toggleAccessForAllSubGroups('enrollmentAllowed', $event)"
                    :valueKey='`enrollment`'
                    label='Enrollment'
                  />
                </div>
              </td>
              <td>
                <div class="cell -acl">
                  <app-checkbox
                    :value="allSubGroupsHaveAcess('claimsAllowed')"
                    @click="toggleAccessForAllSubGroups('claimsAllowed', $event)"
                    :valueKey='`claims`'
                    label='Claims'
                  />
                </div>
              </td>
              <td>
                <div class="cell -acl">
                  <app-checkbox
                    :value="allSubGroupsHaveAcess('viewInvoicesAllowed')"
                    @click="toggleAccessForAllSubGroups('viewInvoicesAllowed', $event)"
                    :valueKey='`viewInvoices`'
                    label='Billing'
                  />
                </div>
              </td>
              <td>
                <div class="cell -acl">
                  <app-checkbox
                    :value="allSubGroupsHaveAcess('createGroupUsersAllowed')"
                    @click="toggleAccessForAllSubGroups('createGroupUsersAllowed', $event)"
                    :valueKey='`createUsers`'
                    label='Admin Management'
                  />
                </div>
              </td>
              <td style="width: 58px">
              </td>
            </tr>
          </table>
        </div>

        <app-acl-table v-for="(item, index) in pendingGroupsWithSubGroups" :key="index" :group="item" @input="updateAcl" @removeGroup="removeGroup" :readonly="readonly" :collapsed="internalCollapsed" />
      </div>
    </template>
  </collapsible-section>
</template>

<script>
  import { computed, onBeforeMount, ref } from '@vue/composition-api';
  import { useRoute, useStore } from '@vue3shims';
  import CollapsibleSection from '@sections/CollapsibleSection'
  import AppAclTable from '@organisms/v2/ACLTable'
  import AppSearchInput from '@atoms/SearchInput'
  import AppCheckbox from '@atoms/Checkbox'

  import groupSelection from '@mixins/groupSelection'

  export default {
    setup(props) {
      const store = useStore();
      const route = useRoute();
      const cloudCannonContent = computed(() => store.getters['cloudCannon/cloudCannonContent']);
      const cloudCannonContentLoading = computed(() => store.getters['cloudCannon/cloudCannonContentLoading']);
      const filterGroups = ref('');
      const internalCollapsed = ref(props.collapsed || true)
      const internalUserRole = computed({
        get() {
          return store.getters['users2/activeUser'].role;
        },
        set(newVal) {
          store.commit('users2/SET_ACTIVE_USER_ROLE', newVal);
        }
      });
      const viewOnly = computed(() => route.path.includes('view'));

      const toggleAll = (collapse) => {
        internalCollapsed.value = collapse;
      }

      onBeforeMount(async () => {
        // console.log(`Pending OBM`, props.value);
        await store.dispatch('cloudCannon/GET_CLOUD_CANNON_CONTENT', {cloudCannonResource: '/user_add_edit_screen.json'});
      });

      return {
        cloudCannonContent,
        cloudCannonContentLoading,
        filterGroups,
        internalCollapsed,
        internalUserRole,
        viewOnly,
        toggleAll,
      }
    },
    mixins: [
      groupSelection
    ],
    components: {
      CollapsibleSection,
      AppAclTable,
      AppSearchInput,
      AppCheckbox
    },
    props: {
      myProfile: {
        type: Boolean,
        default: false
      },
      collapsed: {
        type: Boolean,
        default: true
      },
      value: Object,
      readonly: {
        type: Boolean,
        value: false
      },
      title: {
        type: String,
        required: false,
        default: () => 'Pending Permissions',
      }
    },
    watch: {
      collapsed(newValue) { this.internalCollapsed = newValue }
    },
    computed: {
      groupsSelected() {
        return this.internalUserRole && this.internalUserRole.subGroupAccessPending && this.internalUserRole.subGroupAccessPending.length
      },
      computedUserId() {
        return this.$route.params.userId
      },
      computedGroupsAndSubGroup: {
        get() {
          if (!this.internalUserRole || !this.internalUserRole.subGroupAccessPending) {
            return []
          }

          let subGroupsInGroupWithoutAnyAccess = {}
          let groups = [];
          this.internalUserRole.subGroupAccessPending
            .forEach((sg) => {
            console.log(`in forEach`, groups);
            // groups that have been removed will have all their values set to false, don't show in list
            const hasNoAccess = Object.values(sg.access).filter(access => access === true).length === 0
            // if all subgroups in a group don’t have access to anything, hide the entire group. But don’t hide individual subgroups
            if (hasNoAccess) {
              subGroupsInGroupWithoutAnyAccess[sg.groupCk] = subGroupsInGroupWithoutAnyAccess[sg.groupCk] || []
              subGroupsInGroupWithoutAnyAccess[sg.groupCk].push(sg.subGroupCk)
            }

            if (this.filterGroups !== '') {
              if (
                (sg.groupName || '').toLowerCase().indexOf(this.filterGroups.toLowerCase()) === -1 &&
                (sg.subGroupName || '').toLowerCase().indexOf(this.filterGroups.toLowerCase()) === -1 &&
                (sg.groupId || '').toLowerCase().indexOf(this.filterGroups.toLowerCase()) === -1 &&
                (sg.subGroupId || '').toLowerCase().indexOf(this.filterGroups.toLowerCase()) === -1
              ) {
                return
              }
            }


            let groupIndex = groups.findIndex((g) => g.groupCk === sg.groupCk);
            console.log(`groupIndex`, groupIndex);
            if(groupIndex === -1) {
            // if (!groups.hasOwnProperty(sg.groupCk)) {
              // groups[sg.groupCk] = {
              let group = {
                groupName: sg.groupName,
                groupCk: sg.groupCk,
                groupId: sg.groupId,
                primaryContact: sg.access.primaryContact,
                subGroups: []
              }

              // groups[sg.groupCk].subGroups[sg.subGroupCk] = {
              let subGroup = {
                subGroupCk: sg.subGroupCk,
                subGroupId: sg.subGroupId,
                subGroupName: sg.subGroupName,
                access: {
                  claimsAllowed: sg.access.claimsAllowed,
                  enrollmentAllowed: sg.access.enrollmentAllowed,
                  memberSearchAllowed: sg.access.memberSearchAllowed,
                  memberMaintenanceAllowed: sg.access.memberMaintenanceAllowed,
                  viewInvoicesAllowed: sg.access.viewInvoicesAllowed,
                  createGroupUsersAllowed: sg.access.createGroupUsersAllowed
                }
              }
            group.subGroups.push(subGroup);
            groups.push(group);
            }
            else {
              let group = groups[groupIndex];
              if(group.subGroups.findIndex((sub) => sub.subGroupCk === sg.subGroupCk) === -1) {
                let subGroup = {
                  subGroupCk: sg.subGroupCk,
                  subGroupId: sg.subGroupId,
                  subGroupName: sg.subGroupName,
                  access: {
                    claimsAllowed: sg.access.claimsAllowed,
                    enrollmentAllowed: sg.access.enrollmentAllowed,
                    memberSearchAllowed: sg.access.memberSearchAllowed,
                    memberMaintenanceAllowed: sg.access.memberMaintenanceAllowed,
                    viewInvoicesAllowed: sg.access.viewInvoicesAllowed,
                    createGroupUsersAllowed: sg.access.createGroupUsersAllowed
                  }
                }
                group.subGroups.push(subGroup);
              }
            }
          })

        // if all subgroups in a group don’t have access to anything, hide the entire group. But don’t hide individual subgroups
          for (let i = 0; i < groups.length; i++) {
            let grp = groups[i];
            if(subGroupsInGroupWithoutAnyAccess[grp.groupCk] && grp.subGroups.length === subGroupsInGroupWithoutAnyAccess[grp.groupCk].length) {
              groups.splice(i, 1);
            }
          };
          // Object.keys(groups).forEach((groupCk) => {
          //   const group = groups[groupCk]
          //   if (subGroupsInGroupWithoutAnyAccess[groupCk] && Object.keys(group.subGroups).length === subGroupsInGroupWithoutAnyAccess[groupCk].length) {
          //     delete groups[groupCk]
          //   }
          // });
          console.log(`pending <><><><> groups`, groups);
          return groups;
        }
      },
      pendingGroupsWithSubGroups() {
        // console.log(`filterGroups`, this.filterGroups);
        if (this.$store.getters['users2/activeUserRole']) {
          let rawGroups = this.$store.getters['users2/activeUserRole'].pendingGroupsWithSubGroups || [];
          let filtered = rawGroups.filter((group) => {
            if (this.filterGroups && this.filterGroups || this.filterGroups !== '') {
              return (
                group.groupName.toLowerCase().includes(this.filterGroups.toLowerCase())
                || group.subGroupName.toLowerCase().includes(this.filterGroups.toLowerCase())
                || group.groupId.toLowerCase().includes(this.filterGroups.toLowerCase())
                || group.subGroupId.toLowerCase().includes(this.filterGroups.toLowerCase())
              );
            }
            else {
              return true;
            }
          });
          // console.log(`pendingGroupsWithSubGroups`, filtered);
          return filtered;
        }
        return [];
      }
    },
    methods: {
      removeGroup(groupCk) {
        this.internalUserRole.subGroupAccessPending.forEach((subGroup, index) => {
          if (subGroup.groupCk != groupCk) {
            return
          }

          // for deleteing groups, they requested all the values in the group be set to false
          let newAccess = { ...subGroup.access }
          Object.keys(newAccess).forEach((key) => {
            newAccess[key] = false
          })

          subGroup.access = newAccess

          this.$set(this.internalUserRole.subGroupAccessPending, index, subGroup)
        })

        this.$emit('input', this.internalUserRole)
      },
      updateAcl(newGroupAcl) {
        this.internalUserRole.subGroupAccessPending.forEach((internalSubGroup, index) => {
          const subGroupWithNewAcl = newGroupAcl.subGroups[internalSubGroup.subGroupCk]
          if (!subGroupWithNewAcl) {
            return
          }

          internalSubGroup.access = {
            primaryContact: newGroupAcl.primaryContact,
            claimsAllowed: subGroupWithNewAcl.access.claimsAllowed,
            enrollmentAllowed: subGroupWithNewAcl.access.enrollmentAllowed,
            createGroupUsersAllowed: subGroupWithNewAcl.access.createGroupUsersAllowed,
            memberMaintenanceAllowed: subGroupWithNewAcl.access.memberMaintenanceAllowed,
            memberSearchAllowed: subGroupWithNewAcl.access.memberSearchAllowed,
            viewInvoicesAllowed: subGroupWithNewAcl.access.viewInvoicesAllowed
          }

          this.$set(this.internalUserRole.subGroupAccessPending, index, internalSubGroup)
        })

        this.$emit('input', this.internalUserRole)
      },
      selectAll() {
        this.internalUserRole.subGroupAccessPending.forEach((sg, index) => {
          if (!this.subGroupActionable(sg)) {
            return
          }

          Object.keys(sg.access).forEach((accessKey) => {
            sg.access[accessKey] = true
          })

          this.$set(this.internalUserRole.subGroupAccessPending, index, sg)
        })
      },
      allSubGroupsHaveAcess(accessKey) {
        if (!this.internalUserRole.subGroupAccessPending) {
          return false
        }

        let hasAccess = true
        this.internalUserRole.subGroupAccessPending.forEach((sg, index) => {
          if (!this.subGroupActionable(sg)) {
            return
          }

          if (!sg.access[accessKey]) {
            hasAccess = false
          }
        })

        return hasAccess
      },
      toggleAccessForAllSubGroups(access, $event) {
        this.internalUserRole.subGroupAccessPending.forEach((sg, index) => {
          if (!this.subGroupActionable(sg)) {
            return;
          }

          this.internalUserRole.subGroupAccessPending[index].access[access] = $event;
        });

        this.$emit('input', this.internalUserRole)
      },
      subGroupActionable(sg) {
        // only toggle subgroups that are being displayed in acl table. Some are hidden because every access is set to false
        if (!this.computedGroupsAndSubGroup[sg.groupCk] || !this.computedGroupsAndSubGroup[sg.groupCk].subGroups[sg.subGroupCk]) {
          return false
        }

        return true
      }
    }
  }

</script>
